// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Stack,
} from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';

// shared components
import { AdministratorOnly } from '@/components/access-control';

// components
import { useDatagridLocaleText } from '@/hooks/useDatagridLocaleText';

function MeteringPointsDatagrid({
  meters,
  isLoadingMeters,
  isErrorMeters,
  limitedMeters,
  excludedMeters,
  onToggleLimited,
  onToggleExcluded,
}) {
  const { t } = useTranslation('datahub-integration');
  const localeText = useDatagridLocaleText();

  const columns = [
    {
      field: 'type',
      headerName: t('detail.type'),
      width: 120,
    },
    {
      field: 'address',
      headerName: t('detail.address'),
      flex: 1,
      minWidth: 250,
    },
    {
      field: 'is_child',
      headerName: t('detail.is_child'),
      width: 120,
      renderCell: (params) => {
        if (params.row.is_child) {
          return (
            <Chip label={t('detail.child')} size="small" variant="outlined" />
          );
        }
        if (params.row.has_children) {
          return (
            <Chip
              label={t('detail.children_count', {
                count: params.row.children_count,
              })}
              size="small"
              color="primary"
              variant="outlined"
            />
          );
        }
        return null;
      },
    },
    {
      field: 'access_status',
      headerName: t('detail.access_status'),
      width: 150,
      renderCell: (params) => {
        const isLimited = limitedMeters.includes(params.row.metering_point_id);
        const isExcluded = excludedMeters.includes(
          params.row.metering_point_id,
        );

        if (isExcluded) {
          return (
            <Chip label={t('detail.excluded')} color="error" size="small" />
          );
        }
        if (isLimited) {
          return (
            <Chip label={t('detail.included')} color="success" size="small" />
          );
        }
        if (limitedMeters.length > 0) {
          return (
            <Chip
              label={t('detail.not_included')}
              color="default"
              size="small"
            />
          );
        }
        return (
          <Chip
            label={t('detail.all_included')}
            color="success"
            size="small"
            variant="outlined"
          />
        );
      },
    },
    {
      field: 'status',
      headerName: t('detail.status'),
      width: 100,
      renderCell: () => {
        return <Chip label={t('detail.active')} color="success" size="small" />;
      },
    },
    {
      field: 'actions',
      headerName: t('detail.actions'),
      width: 250,
      renderCell: (params) => {
        const meterId = params.row.metering_point_id;
        const isLimited = limitedMeters.includes(meterId);
        const isExcluded = excludedMeters.includes(meterId);

        return (
          <Stack direction="row" spacing={1} alignItems="center" height="100%">
            <Button
              size="small"
              variant={isLimited ? 'contained' : 'outlined'}
              color="success"
              onClick={() => onToggleLimited(meterId)}
              disabled={excludedMeters.length > 0 && !isLimited}
            >
              {isLimited
                ? t('detail.remove_from_include')
                : t('detail.include')}
            </Button>
            <Button
              size="small"
              variant={isExcluded ? 'contained' : 'outlined'}
              color="error"
              onClick={() => onToggleExcluded(meterId)}
              disabled={limitedMeters.length > 0 && !isExcluded}
            >
              {isExcluded
                ? t('detail.remove_from_exclude')
                : t('detail.exclude')}
            </Button>
          </Stack>
        );
      },
    },
  ];

  const rows = [];

  meters?.forEach((meter) => {
    const hasChildren =
      meter.childMeteringPoints && meter.childMeteringPoints.length > 0;

    // Add parent meter
    rows.push({
      id: meter.meteringPointId,
      metering_point_id: meter.meteringPointId,
      type: meter.typeOfMP || t('detail.unknown'),
      address: meter.streetName
        ? `${meter.streetName} ${meter.buildingNumber || ''}, ${meter.postcode || ''} ${meter.cityName || ''}`
        : 'N/A',
      is_child: false,
      has_children: hasChildren,
      children_count: hasChildren ? meter.childMeteringPoints.length : 0,
      hierarchy: [meter.meteringPointId],
    });

    // Add child meters
    if (hasChildren) {
      meter.childMeteringPoints.forEach((child) => {
        rows.push({
          id: child.meteringPointId,
          metering_point_id: child.meteringPointId,
          type: child.typeOfMP || t('detail.unknown'),
          address: child.streetName
            ? `${child.streetName} ${child.buildingNumber || ''}, ${child.postcode || ''} ${child.cityName || ''}`
            : 'N/A',
          is_child: true,
          has_children: false,
          children_count: 0,
          hierarchy: [meter.meteringPointId, child.meteringPointId],
        });
      });
    }
  });

  return (
    <Card>
      <CardHeader
        title={t('detail.meters_title')}
        subheader={t('detail.meters_subtitle')}
      />
      <AdministratorOnly
        fallback={<Alert severity="info">{t('detail.require_admin')}</Alert>}
      >
        <CardContent>
          {isErrorMeters ? (
            <Alert severity="error">{t('detail.error_loading_meters')}</Alert>
          ) : (
            <Box sx={{ height: 600, width: '100%' }}>
              <DataGridPremium
                rows={rows}
                columns={columns}
                loading={isLoadingMeters}
                disableRowSelectionOnClick
                treeData
                getTreeDataPath={(row) => row.hierarchy}
                groupingColDef={{
                  headerName: t('detail.metering_point_id'),
                  width: 250,
                }}
                defaultGroupingExpansionDepth={-1}
                localeText={localeText}
                initialState={{
                  pagination: {
                    paginationModel: { pageSize: 25 },
                  },
                }}
                pageSizeOptions={[10, 25, 50, 100]}
              />
            </Box>
          )}
        </CardContent>
      </AdministratorOnly>
    </Card>
  );
}

export default MeteringPointsDatagrid;
